import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import {
  canRetry, formatDay, outcomeText, outcomeTone, rowsOf, stateText,
} from "@/lib/publishosView";
import { tr } from "@/lib/i18n";

/**
 * What PublishOS did with a newsletter's ads, one line per ad, plus the
 * two things the editor can do about it:
 *   - answer "pull the future booking forward, or create a new one?"
 *   - Retry (after a failure, or after linking an advertiser)
 *
 * `report` is `newsletter.publishos`; with `startable` and no report it
 * shows a single "Report to PublishOS" button (newsletters that went out
 * before the link existed). `onChange` receives the updated report.
 */
const TONES = {
  ok: "text-[var(--ink)]",
  warn: "text-amber-700",
  bad: "text-red-700",
  muted: "text-[var(--ink-muted)]",
};

export default function PublishOSReport({ newsletterId, report, startable = false, onChange }) {
  const qc = useQueryClient();
  const [current, setCurrent] = useState(report || null);
  useEffect(() => setCurrent(report || null), [report]);

  const run = useMutation({
    mutationFn: (decisions) => api.reportNewsletterToPublishOS(newsletterId, decisions || {}),
    onSuccess: (next) => {
      setCurrent(next);
      if (onChange) onChange(next);
      if (next?.summary) toast.message(next.summary);
      qc.invalidateQueries({ queryKey: ["newsletter-detail", newsletterId] });
      qc.invalidateQueries({ queryKey: ["newsletters"] });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || tr("Couldn't report to PublishOS")),
  });

  if (!current) {
    if (!startable) return null;
    return (
      <div data-testid="publishos-report">
        <h4 className="text-[11px] font-mono-display uppercase tracking-wider text-[var(--ink-muted)] mb-1.5">{tr("PublishOS")}</h4>
        <button
          type="button"
          data-testid="publishos-report-start"
          onClick={() => run.mutate({})}
          disabled={run.isPending}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[var(--border-strong)] hover:bg-[var(--secondary)] disabled:opacity-50"
        >
          {run.isPending ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5" />
          )}{tr("Report these ads to PublishOS")}</button>
      </div>
    );
  }

  const rows = rowsOf(current);
  const heading = stateText(current);
  return (
    <div data-testid="publishos-report">
      <h4 className="text-[11px] font-mono-display uppercase tracking-wider text-[var(--ink-muted)] mb-1.5">{tr("PublishOS{value}", { value: current.sent_date ? tr(" · sent {when}", { when: formatDay(current.sent_date) }) : "" })}
      </h4>
      {heading && (
        <p
          className={`text-xs mb-1.5 ${
            current.state === "failed"
              ? TONES.bad
              : current.state === "needs_decision"
                ? TONES.warn
                : TONES.muted
          }`}
        >
          {heading}
        </p>
      )}
      <ul className="space-y-1.5">
        {rows.map((row) => (
          <li
            key={row.key}
            data-testid={`publishos-row-${row.key}`}
            className="text-xs border border-[var(--border)] px-2 py-1.5"
          >
            <div className="text-[var(--ink)]">
              <span className="font-semibold">{row.name || tr("(unnamed)")}</span>
              {row.client_name ? (
                <span className="text-[var(--ink-muted)]"> → {row.client_name}</span>
              ) : null}
              {row.product_name ? (
                <span className="text-[var(--ink-muted)]"> · {row.product_name}</span>
              ) : null}
            </div>
            <div className={TONES[outcomeTone(row)]}>{outcomeText(row)}</div>
            {row.outcome === "needs_decision" && (
              <div className="flex flex-wrap gap-2 mt-1.5">
                <button
                  type="button"
                  data-testid={`publishos-pull-${row.key}`}
                  onClick={() => run.mutate({ [row.key]: "pull" })}
                  disabled={run.isPending}
                  className="px-2 py-1 border border-[var(--border-strong)] hover:bg-[var(--secondary)] disabled:opacity-50"
                >{tr("Use that booking (move it to the send date)")}</button>
                <button
                  type="button"
                  data-testid={`publishos-new-${row.key}`}
                  onClick={() => run.mutate({ [row.key]: "new" })}
                  disabled={run.isPending}
                  className="px-2 py-1 border border-[var(--border-strong)] hover:bg-[var(--secondary)] disabled:opacity-50"
                >{tr("Leave it and create a new booking")}</button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {canRetry(current) && (
        <button
          type="button"
          data-testid="publishos-report-retry"
          onClick={() => run.mutate({})}
          disabled={run.isPending}
          className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 text-xs border border-[var(--border-strong)] hover:bg-[var(--secondary)] disabled:opacity-50"
        >
          {run.isPending ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5" />
          )}{tr("Retry")}</button>
      )}
    </div>
  );
}
